import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type IncentiveFundBalancesView, type IncentiveFundEnvelopeSide, type IncentiveFundInvestmentView, type IncentiveFundMoney, type IncentiveFundSpendWindow } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import {
  maySelfApprove,
  FUND_SIDE_ORDER,
  INVESTMENT_REASONS,
  INVESTMENT_VALIDITY_DAYS,
  fundTotals,
  formatMarketInstant,
  incentiveFundAuthority,
  investmentApproveBlock,
  investmentDraftIssue,
  investmentTone,
  investmentValidUntil,
  marketTimeZone,
  policyTone,
  spendMeter,
  splitEnvelopes,
  type EnvelopeRow,
} from '../../src/index';
import { useAdmin } from '../session';
import { PersonName } from '../components/person-name';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, EmptyState, Field, FormRow, Grid, Meter, MoneyInput, Notice, PageHeader, Panel, Row, Select, Stack, StatCard, Table, Text, TextInput } from '@vello/web-theme';

/**
 * Finance › Fundo de Incentivos (W10-INCENTIVE-FUND, D489b, ADR-0102; doc 17 section 3.1), over `IncentiveFundAdminApi`
 * (`/v1/admin/incentive-fund/*`).
 *
 * For the market the operator picks (from his scope - never typed):
 * - **the policy**: whether the market publishes `incentives.fundPolicy` (NO policy, NO envelope, nothing credited or spent) and how far the accrual from
 *   the economic facts has come;
 * - **the envelopes** of the market's sides - credited, reserved, confirmed, available and how much is already promised - with exact per-currency
 *   totals, and the country's own envelope apart;
 * - **the last 24 hours' and the last 7 days' spend** (D839: rolling windows) against the caps the policy publishes, with the contribution floor ("never lose money");
 * - **the owner's investments** under four eyes: the maker proposes a deposit (an envelope, an amount in the market's currency, a reason, a validity), a
 *   SECOND operator approves or rejects it. Approve is hidden from the proposer (the server refuses `FUND_SELF_APPROVAL` anyway) and from one without
 *   `incentive_fund:approve`, with the reason shown; a rejection needs a reason.
 * Money is typed through the design-system `MoneyInput` in the market's currency (never minor units, never a typed currency). A refused or failed read is an
 * ERROR - never an empty list or a zero. No native dialog is ever opened. The fund is a management budget: no figure here is a balance a customer sees.
 */

type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string } | undefined;
type ReasonChoice = (typeof INVESTMENT_REASONS)[number];

const note = (text: string): ReactElement => <EmptyState title={text} />;

export function IncentiveFundScreen(): ReactElement {
  const { runtime, markets, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { fail, v, refusal: refusalOf } = useVocab();
  const { mayView, mayInvest, mayApprove } = incentiveFundAuthority(grants);
  // Owner 2026-10-12: the market of the fund IS the header scope's city - no second market selector on this screen.
  const { market: scopeMarket, choices } = useScopeMarket();
  const market = scopeMarket ?? '';
  const [loaded, setLoaded] = useState('');
  const [view, setView] = useState<IncentiveFundBalancesView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState(false);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  // the proposal draft
  const [side, setSide] = useState<IncentiveFundEnvelopeSide>('ACQUISITION');
  const [amount, setAmount] = useState<bigint | undefined>(undefined);
  const [reason, setReason] = useState<ReasonChoice | ''>('');
  const [days, setDays] = useState<number>(INVESTMENT_VALIDITY_DAYS[0]);
  const [draftNote, setDraftNote] = useState('');
  const seq = useRef(0);

  const refusal = useCallback(
    (e: { code: string; reasonCode?: string; detail?: string }, fallback: string): string => {
      return refusalOf('refusal.admin.incentive_fund', e) ?? t(fallback, { code: fail(e) });
    },
    [t, refusalOf],
  );

  const load = useCallback(
    async (m: string): Promise<void> => {
      const mine = ++seq.current;
      const target = m.trim();
      setLoaded(target);
      setView(undefined);
      setError(undefined);
      if (target === '' || !mayView) return;
      const res = await runtime.incentiveFund.balances(target);
      if (mine !== seq.current) return;
      if (res.ok) setView(res.value);
      else setError(refusal(res.error, 'fund.error'));
    },
    [runtime, mayView, refusal],
  );

  useEffect(() => {
    void load(market);
    // Reload when the header scope names another market.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);

  const currency = view?.policy.currency ?? '';
  const tz = marketTimeZone(loaded, markets);
  const when = (iso: string | null | undefined): string => (iso === null || iso === undefined ? '—' : formatMarketInstant(iso, tz, lang));
  const money = useCallback(
    (m: IncentiveFundMoney | null | undefined): string =>
      m === null || m === undefined ? '—' : /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency) ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) : `${m.amountMinor} ${m.currency}`,
    [lang],
  );
  const minorMoney = (amountMinor: bigint, cur: string): string => money({ amountMinor: amountMinor.toString(), currency: cur });

  const split = view !== undefined ? splitEnvelopes(view) : undefined;
  const totals = split !== undefined ? fundTotals(split.market) : [];
  const issue = investmentDraftIssue({ side, amountMinor: amount, reasonCode: reason, validityDays: days, note: draftNote });
  const canPropose = mayInvest && view?.policy.state === 'ACTIVE' && issue === undefined && !busy;

  const propose = async (): Promise<void> => {
    if (reason === '' || amount === undefined) return;
    setBusy(true);
    const res = await runtime.incentiveFund.proposeInvestment({
      marketId: loaded, side, amountMinor: amount.toString(), reasonCode: reason, validUntil: investmentValidUntil(new Date().toISOString(), days), ...(draftNote.trim() !== '' ? { note: draftNote.trim() } : {}),
    });
    setBusy(false);
    if (!res.ok) return setMsg({ tone: 'danger', text: refusal(res.error, 'fund.error') });
    setAmount(undefined);
    setDraftNote('');
    setMsg({ tone: 'success', text: t('fund.propose.sent') });
    await load(loaded);
  };

  const decide = async (inv: IncentiveFundInvestmentView, decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    const why = (reasons[inv.investmentId] ?? '').trim();
    if (decision === 'REJECT' && why.length < 3) return setMsg({ tone: 'warning', text: t('fund.decision.reason') });
    setBusy(true);
    const res = await runtime.incentiveFund.decideInvestment(inv.investmentId, { marketId: loaded, decision, ...(why !== '' ? { reason: why } : {}) });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusal(res.error, 'fund.error') });
      await load(loaded);
      return;
    }
    setMsg({ tone: 'success', text: t(decision === 'APPROVE' ? 'fund.decision.approved' : 'fund.decision.rejected') });
    await load(loaded);
  };

  const sideLabel = (s: string): string => v('incentive_fund.side', s);
  const blockText = (b: ReturnType<typeof investmentApproveBlock>): string | undefined => (b === 'OWN_PROPOSAL' ? t('fund.approve.own') : b === 'NO_PERMISSION' ? t('fund.approve.noPermission') : undefined);

  const decisionCell = (inv: IncentiveFundInvestmentView): ReactElement => {
    const block = investmentApproveBlock(inv, me?.id, mayApprove, selfApproval);
    if (block === 'NOT_PENDING') return <span>{inv.decisionReason ?? '—'}</span>;
    return (
      <Stack gap={2}>
        <TextInput aria-label={t('fund.decision.reason')} placeholder={t('fund.decision.reason')} value={reasons[inv.investmentId] ?? ''} maxLength={500} onChange={(ev) => setReasons((r) => ({ ...r, [inv.investmentId]: ev.target.value }))} />
        <Row gap={2} align="center" wrap>
          {block === undefined && <Button type="button" variant="success" disabled={busy} onClick={() => void decide(inv, 'APPROVE')}>{t('fund.decision.approve')}</Button>}
          {mayApprove || inv.proposedBy === me?.id ? <Button type="button" variant="danger" disabled={busy} onClick={() => void decide(inv, 'REJECT')}>{t('fund.decision.reject')}</Button> : null}
        </Row>
        {blockText(block) !== undefined && <Text role="caption" tone="tertiary">{blockText(block)}</Text>}
      </Stack>
    );
  };

  const windowMeter = (label: string, w: IncentiveFundSpendWindow): ReactElement => {
    const m = spendMeter(w);
    const share = `${Math.floor(w.capBp / 100)}${w.capBp % 100 === 0 ? '' : `,${String(w.capBp % 100).padStart(2, '0')}`}%`;
    return (
      <Stack gap={1}>
        <Meter
          label={label}
          value={m.usedBp}
          max={10_000}
          showMax={false}
          warnAt={0.8}
          dangerAt={1}
          warnLabel={t('fund.spend.near')}
          dangerLabel={t('fund.spend.over')}
          formatValue={(v) => `${Math.floor(v / 100)}%`}
        />
        <Text role="caption" tone="tertiary"><span className="tnum">{t('fund.spend.line', { cap: money(w.cap), share, gross: money(w.gross), spent: money(w.spent), remaining: money(w.remaining) })}</span></Text>
      </Stack>
    );
  };

  const envelopeColumns = [
    { key: 'side', header: t('fund.col.side'), render: (r: EnvelopeRow) => <span>{sideLabel(r.side)}</span> },
    { key: 'credited', header: t('fund.col.credited'), numeric: true, render: (r: EnvelopeRow) => <span className="tnum">{minorMoney(r.creditedMinor, r.currency)}</span> },
    { key: 'reserved', header: t('fund.col.reserved'), numeric: true, render: (r: EnvelopeRow) => <span className="tnum">{minorMoney(r.reservedMinor, r.currency)}</span> },
    { key: 'confirmed', header: t('fund.col.confirmed'), numeric: true, render: (r: EnvelopeRow) => <span className="tnum">{minorMoney(r.confirmedMinor, r.currency)}</span> },
    { key: 'available', header: t('fund.col.available'), numeric: true, render: (r: EnvelopeRow) => <span className="tnum">{minorMoney(r.availableMinor, r.currency)}</span> },
    { key: 'committed', header: t('fund.col.committed'), numeric: true, render: (r: EnvelopeRow) => <span className="tnum">{Math.floor(r.committedBp / 100)}%</span> },
  ];

  return (
    <>
      <PageHeader title={t('fund.title')} subtitle={t('fund.subtitle')} />
      {!mayView ? (
        <Panel>{note(t('fund.noPermission'))}</Panel>
      ) : (
        <>
          {(error !== undefined || msg !== undefined) && (
            <Panel>
              {error !== undefined && <Notice tone="danger">{error}</Notice>}
              {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
            </Panel>
          )}

          {scopeMarket === undefined && <ScopeMarketNotice choices={choices} />}

          {view !== undefined && split !== undefined && (
            <>
              <Panel>
                <Stack gap={3}>
                  <Row gap={2} align="center" wrap>
                    <Badge tone={policyTone(view.policy.state)}>{v('incentive_fund.policy_state', view.policy.state)}</Badge>
                    <Text role="caption" tone="tertiary">{t('fund.policy.version', { version: view.policy.policyVersion })}</Text>
                  </Row>
                  {view.policy.state !== 'ACTIVE' && <Notice tone={view.policy.state === 'INVALID' ? 'danger' : 'warning'}>{v('incentive_fund.policy_state_note', view.policy.state)}</Notice>}
                  <Text role="caption" tone="tertiary">
                    {view.accrual === undefined || view.accrual.state === 'NOT_STARTED'
                      ? t('fund.accrual.notStarted')
                      : t('fund.accrual.running', { read: view.accrual.factsRead, before: view.accrual.factsBeforePolicy, when: when(view.accrual.updatedAt) })}
                  </Text>
                  <Text role="caption" tone="tertiary">{t('fund.accrual.rule')}</Text>
                  {view.policy.state === 'ACTIVE' && <Text role="caption" tone="tertiary">{t(view.policy.marketCouponsFromFund === true ? 'fund.coupons.fromFund' : 'fund.coupons.monthly')}</Text>}
                </Stack>
              </Panel>

              {totals.length > 0 && (
                <Grid min="sm" gap={4}>
                  <StatCard label={t('fund.summary.credited')} value={totals.map((x) => minorMoney(x.creditedMinor, x.currency)).join(' · ')} />
                  <StatCard label={t('fund.summary.reserved')} value={totals.map((x) => minorMoney(x.reservedMinor, x.currency)).join(' · ')} />
                  <StatCard label={t('fund.summary.confirmed')} value={totals.map((x) => minorMoney(x.confirmedMinor, x.currency)).join(' · ')} />
                  <StatCard label={t('fund.summary.available')} value={totals.map((x) => minorMoney(x.availableMinor, x.currency)).join(' · ')} />
                </Grid>
              )}

              <Panel padded={false}>
                <Table columns={envelopeColumns} rows={split.market} rowKey={(r) => r.envelopeKey} empty={note(t('fund.envelopes.empty'))} />
              </Panel>

              {split.country !== undefined && (
                <Panel padded={false}>
                  <Table columns={envelopeColumns} rows={[split.country]} rowKey={(r) => r.envelopeKey} />
                  <Text role="caption" tone="tertiary">{t('fund.country.note')}</Text>
                </Panel>
              )}

              {view.policy.state === 'ACTIVE' && (
                <Panel>
                  <Stack gap={3}>
                    <h3>{t('fund.spend.title')}</h3>
                    {view.spend === undefined || view.spend === null ? (
                      <Text role="caption" tone="tertiary">{t('fund.spend.unavailable')}</Text>
                    ) : (
                      <>
                        {windowMeter(t('fund.spend.day'), view.spend.day)}
                        {windowMeter(t('fund.spend.week'), view.spend.week)}
                        <Text role="caption" tone="tertiary"><span className="tnum">{t('fund.spend.floor', { after: money(view.spend.week.contributionAfterSpend), required: money(view.spend.week.floorRequired) })}</span></Text>
                      </>
                    )}
                  </Stack>
                </Panel>
              )}

              <Panel padded={false}>
                <Stack gap={3}>
                  <h3>{t('fund.investments.title')}</h3>
                  <Text role="caption" tone="tertiary">{t('fund.investments.rule')}</Text>
                  <Table
                    columns={[
                      {
                        key: 'what', header: t('fund.col.what'),
                        render: (i: IncentiveFundInvestmentView) => (
                          <span className="tnum">
                            {t('fund.investment.line', { side: sideLabel(i.side), amount: money(i.amount), reason: v('incentive_fund.investment_reason', i.reasonCode) })}
                            {i.status === 'RETURNED' || i.returned.amountMinor !== '0' ? ` · ${t('fund.investment.returned', { amount: money(i.returned) })}` : ''}
                          </span>
                        ),
                      },
                      { key: 'by', header: t('fund.col.proposedBy'), render: (i: IncentiveFundInvestmentView) => <span>{i.proposedByLabel ?? <PersonName id={i.proposedBy} />} · <span className="tnum">{when(i.proposedAt)}</span></span> },
                      { key: 'until', header: t('fund.col.validUntil'), render: (i: IncentiveFundInvestmentView) => <span className="tnum">{when(i.validUntil)}</span> },
                      { key: 'status', header: t('fund.col.status'), render: (i: IncentiveFundInvestmentView) => <Badge tone={investmentTone(i.status)}>{v('incentive_fund.investment_status', i.status)}</Badge> },
                      { key: 'decision', header: t('fund.col.decision'), render: decisionCell },
                    ]}
                    rows={view.investments}
                    rowKey={(i) => i.investmentId}
                    empty={note(t('fund.investments.empty'))}
                  />
                </Stack>
              </Panel>

              {mayInvest && (
                <Panel>
                  <Stack gap={3}>
                    <h3>{t('fund.propose.title')}</h3>
                    {view.policy.state !== 'ACTIVE' ? (
                      <Text role="caption" tone="tertiary">{t('fund.propose.needsPolicy')}</Text>
                    ) : (
                      <FormRow action={<Button type="button" disabled={!canPropose} onClick={() => void propose()}>{t('fund.propose.submit')}</Button>}>
                        <Field label={t('fund.propose.side')} htmlFor="fund-side">
                          <Select id="fund-side" value={side} onChange={(ev) => setSide(ev.target.value as IncentiveFundEnvelopeSide)}>
                            {[...FUND_SIDE_ORDER, 'COUNTRY' as const].map((s) => (
                              <option key={s} value={s}>{sideLabel(s)}</option>
                            ))}
                          </Select>
                        </Field>
                        {isIso4217Currency(currency) && (
                          <MoneyInput id="fund-amount" label={t('fund.propose.amount')} currency={currency} locale={lang} valueMinor={amount} onChangeMinor={setAmount} issueText={(i) => v('format.money_input_issue', i)} />
                        )}
                        <Field label={t('fund.propose.reason')} htmlFor="fund-reason">
                          <Select id="fund-reason" value={reason} onChange={(ev) => setReason((INVESTMENT_REASONS as readonly string[]).includes(ev.target.value) ? (ev.target.value as ReasonChoice) : '')}>
                            <option value="">{t('fund.propose.reason.choose')}</option>
                            {INVESTMENT_REASONS.map((r) => (
                              <option key={r} value={r}>{v('incentive_fund.investment_reason', r)}</option>
                            ))}
                          </Select>
                        </Field>
                        <Field label={t('fund.propose.validity')} htmlFor="fund-validity">
                          <Select id="fund-validity" value={String(days)} onChange={(ev) => setDays(Number.parseInt(ev.target.value, 10))}>
                            {INVESTMENT_VALIDITY_DAYS.map((d) => (
                              <option key={d} value={String(d)}>{t('fund.propose.validity.days', { days: String(d) })}</option>
                            ))}
                          </Select>
                        </Field>
                        <Field label={t('fund.propose.note')} htmlFor="fund-note" hint={t('fund.propose.note.hint')}>
                          <TextInput id="fund-note" value={draftNote} maxLength={500} onChange={(ev) => setDraftNote(ev.target.value)} />
                        </Field>
                      </FormRow>
                    )}
                  </Stack>
                </Panel>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
